import React, { useEffect, useRef, useState } from 'react';
import { Seo } from './components/Seo';
import { MetaPixel } from './components/MetaPixel';
import { BundleMockup, BonusCover } from './components/BundleMockup';
import { scrollToOffer, goToCheckout } from './lib/checkout';
import { PRICE, ORIGINAL_PRICE, BONUS_IMAGES, SUPPORT_EMAIL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from './data/config';
import { VOLUMES, PAINS, INSIDE, PREVIEW, BONUSES, STEPS, FAQ, Volume } from './data/content';
const BONUS_TOTAL = BONUSES.reduce((s, b) => s + b.value, 0);
const TOTAL_VALUE = ORIGINAL_PRICE + BONUS_TOTAL;
function Label({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return <p className={`text-[11px] font-bold uppercase tracking-[0.22em] ${light ? 'text-terra' : 'text-sage'}`} data-id="element-0" data-mp="2wjgqlh49rtfw">{children}</p>;
}

// Scroll-only button (never goes to checkout).
function SeeButton({
  text
}: {
  text: string;
}) {
  return <button type="button" onClick={scrollToOffer} className="w-full rounded-full bg-terra px-5 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_6px_0_#b85a42] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_3px_0_#b85a42] sm:text-xl" data-id="element-1" data-mp="23ujlygszdv5m">
      {text} ↓
    </button>;
}
function VolumeItem({
  v,
  open,
  onToggle
}: {
  v: Volume;
  open: boolean;
  onToggle: () => void;
}) {
  const start = parseInt(v.range.replace(/[^0-9–]/g, '').split('–')[0], 10);
  return <div className="card overflow-hidden" data-id="element-2" data-mp="2zmo6bandcp36">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 p-4 text-left" aria-expanded={open} data-id="element-3" data-mp="33moqzd129wio">
        <span className="font-display flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sage text-xl font-extrabold text-cream" data-id="element-4" data-mp="38li6tc3tlzm0">{v.num}</span>
        <span className="min-w-0 flex-1" data-id="element-5" data-mp="3dii5v329623m">
          <span className="block text-[10px] font-bold tracking-[0.15em] text-muted" data-id="element-6" data-mp="nuhg0bzipzr4">VOLUME {v.num} · {v.range.toUpperCase()}</span>
          <span className="font-display block text-lg font-bold leading-tight sm:text-xl" data-id="element-7" data-mp="sssr0q1jugax">{v.title}</span>
          <span className="block text-xs italic text-ink/70" data-id="element-8" data-mp="38kwyktgo3k7">“{v.pack}”</span>
        </span>
        <span className={`shrink-0 text-2xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-9" data-mp="3ucjcjtti8btq">+</span>
      </button>
      {open && <div className="border-t border-line bg-creamy/60 p-4" data-id="element-10" data-mp="85ul7x54696g">
          <p className="text-sm text-ink/85" data-id="element-11" data-mp="83i23gnmdsrt">{v.summary}</p>
          <ol className="mt-3 grid gap-x-4 gap-y-1.5 sm:grid-cols-2" data-id="element-12" data-mp="3gmfvdh9hf8b5">
            {v.sessions.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-13" data-mp="3bncnzq614q00" data-mpi={i}>
                <span className="shrink-0 text-[12px] font-bold text-terra" data-id="element-14" data-mp="3qaxs9nen4v9i">{String(start + i).padStart(2, '0')}</span>
                <span className="min-w-0 break-words" data-id="element-15" data-mp="3lk7m47ye4odr">{s}</span>
              </li>)}
          </ol>
          <p className="mt-3 text-xs font-semibold text-sage" data-id="element-16" data-mp="2xuddy3p0nnzu">+ 13 matching client worksheets</p>
        </div>}
    </div>;
}
function FaqItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-line" data-id="element-17" data-mp="18c6p87f6dygh">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-3 py-4 text-left" aria-expanded={open} data-id="element-18" data-mp="1d4hqpepkdsb5">
        <span className="font-semibold" data-id="element-19" data-mp="yh6erbph06ai">{q}</span>
        <span className={`shrink-0 text-xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-20" data-mp="13eew5a2so6ap">+</span>
      </button>
      {open && <p className="pb-4 text-sm leading-relaxed text-ink/85" data-id="element-21" data-mp="1r4sfmc7bcwto">{a}</p>}
    </div>;
}

// Mobile-only sticky bar: scrolls to #offer, hidden near the top and while the offer is visible.
function StickyBar() {
  const [show, setShow] = useState(false);
  const offerVisible = useRef(false);
  const scrolled = useRef(false);
  useEffect(() => {
    const update = () => setShow(scrolled.current && !offerVisible.current);
    const onScroll = () => {
      scrolled.current = window.scrollY > 520;
      update();
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const el = document.getElementById('offer');
    let io: IntersectionObserver | null = null;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(entries => {
        offerVisible.current = entries[0].isIntersecting;
        update();
      }, {
        threshold: 0.05
      });
      io.observe(el);
    }
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      io?.disconnect();
    };
  }, []);
  return <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-3 py-2 backdrop-blur transition-transform duration-300 md:hidden ${show ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-22" data-mp="14wnbqwme0zst">
      
    </div>;
}
export function App() {
  const [openVol, setOpenVol] = useState<number>(0);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#fffdf9]" data-id="element-23" data-mp="3kob2ubepzy3p">
      <Seo data-id="element-24" data-mp="3gmpjc77oklkj" />
      <MetaPixel data-id="element-25" data-mp="3glxxho3vy27g" />

      <header className="border-b border-line bg-white" data-id="element-26" data-mp="3glel0mx0860t">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3" data-id="element-27" data-mp="1w4ezeced2rhs">
          <p className="font-display text-lg font-extrabold tracking-wide text-sage" data-id="element-28" data-mp="2060c9xqwb5rz">
            SIGMA SMART<span className="text-terra" data-id="element-29" data-mp="2553gxh2yr9zf">™</span>
          </p>
          <p className="truncate text-sm font-extrabold uppercase tracking-wide text-terra" data-id="element-30" data-mp="253k2lw0vb90a">Coaches</p>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="bg-sagegrad text-white" data-id="element-31" data-mp="2evfz52nb2r2e">
        <div className="mx-auto grid max-w-5xl items-center gap-8 px-4 py-10 md:grid-cols-2 md:py-16" data-id="element-32" data-mp="2eun1cr6brbwe">
          <div className="min-w-0" data-id="element-33" data-mp="2js6h5vio7wqg">
            <span className="pill bg-white/15 text-cream" data-id="element-34" data-mp="2nvuoqe7aysbp">FOR LIFE, CAREER, EXECUTIVE & WELLNESS COACHES</span>
            <h1 className="font-display mt-4 text-[40px] font-extrabold leading-[1.02] sm:text-6xl" data-id="element-35" data-mp="2sv7hffkuutm7">
              Stop planning sessions from scratch.
              <br data-id="element-36" data-mp="tjobzayrg3ha" />
              <span className="text-terra" data-id="element-37" data-mp="ygoi1lvm168e">Start coaching.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/90 sm:text-lg" data-id="element-38" data-mp="yir6i1h46rb0">
              <strong className="text-white" data-id="element-39" data-mp="21ru85ih3wnhz">78 ready-to-run coaching sessions</strong>, each with its own <strong className="text-white" data-id="element-40" data-mp="1wu263vmqzl02">client worksheet</strong>: goals, mindset, stress,
              habits, boundaries, leadership and career. Open it, pick a session, and coach.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-white/90" data-id="element-41" data-mp="25rmahdfwpxew">
              <li data-id="element-42" data-mp="2kpn23v9r5n38">✔ 6 volumes · 156 sessions & worksheets</li>
              <li data-id="element-43" data-mp="2frd3faotk4cv">✔ For 1:1, groups and online sessions</li>
              <li data-id="element-44" data-mp="2fqtvmmsvuwck">✔ + 4 bonuses included free</li>
            </ul>
          </div>
          <div className="min-w-0" data-id="element-45" data-mp="2thoy9hcsgh7p">
            <BundleMockup data-id="element-46" data-mp="2tkulss5rrb1b" />
            <div className="mt-4" data-id="element-47" data-mp="2pheri2h69mrs">
              <SeeButton text="See the complete system now" data-id="element-48" data-mp="2pi7t6p19xm32" />
            </div>
            <p className="mt-3 text-center text-[12px] text-white/75" data-id="element-49" data-mp="33gedf2davnk9">Instant PDF access · 7-day risk-free guarantee</p>
          </div>
        </div>
      </section>

      {/* 2. SOUND FAMILIAR */}
      <section className="bg-creamy" data-id="element-50" data-mp="3i33g1d2umguv">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-51" data-mp="3i462n8t51x4z">
          <Label data-id="element-52" data-mp="3n39b738bulhp">Sound familiar?</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-53" data-mp="3n27mhvye20hi">You love coaching. Not rebuilding sessions every week.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-54" data-mp="3d4a07e03adrj">
            {PAINS.map(p => <div key={p.title} className="card min-w-0 p-5" data-id="element-55" data-mp="2zd54gmzue5mh">
                <span className="pill bg-sage/10 text-sage" data-id="element-56" data-mp="2zbu5d0hcdm1p">{p.tag}</span>
                <h3 className="font-display mt-3 text-xl font-bold leading-tight" data-id="element-57" data-mp="344wwl1yncjeq">{p.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-58" data-mp="2pg0hiky6a64m">{p.text}</p>
              </div>)}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-lg" data-id="element-59" data-mp="2uf3le22f26b0">
            You already have the skills. This gives you the sessions, written and ready, so your prep time goes back to your clients and your life.
          </p>
        </div>
      </section>

      {/* 3. VOLUMES */}
      <section className="bg-white" data-id="element-60" data-mp="30vndk73ovof1">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-61" data-mp="30uly7qfn4fri">
          <Label data-id="element-62" data-mp="2vw0xzr0e1w32">6 volumes · 78 sessions · 78 worksheets</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-63" data-mp="2vug5s81jqp8d">A session ready for whatever your client brings.</h2>
          <p className="mt-3 text-ink/80" data-id="element-64" data-mp="2lx32g44jp2ak">Open each volume to see all 13 session titles.</p>
          <div className="mt-8 space-y-4" data-id="element-65" data-mp="2hwrg8mwjatwz">
            {VOLUMES.map((v, i) => <VolumeItem key={v.num} v={v} open={openVol === i} onToggle={() => setOpenVol(openVol === i ? -1 : i)} data-id="element-66" data-mp="2d5t1vsv3w5wd" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* 4. INSIDE EVERY SESSION + PREVIEW */}
      <section className="bg-sagegrad text-white" data-id="element-67" data-mp="237vd949c4738">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-68" data-mp="239g7stvnfq13">
          <Label light data-id="element-69" data-mp="1y91wn2yr3zyu">Inside every session</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-70" data-mp="1y7qxxhk5xiel">Everything you need to run it, on one page.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-71" data-mp="9pfliy6wtcls">
            {INSIDE.map((p, i) => <div key={p.title} className="min-w-0 rounded-2xl border border-white/20 bg-white/10 p-5" data-id="element-72" data-mp="dpyh93bxb2t9" data-mpi={i}>
                <p className="text-[11px] font-bold text-terra" data-id="element-73" data-mp="ip2rzyehx8lp">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-display mt-1 text-xl font-bold" data-id="element-74" data-mp="np74ldca9j6n">{p.title}</h3>
                <p className="mt-1 text-sm text-white/85" data-id="element-75" data-mp="3m6kvxur5dvgu">{p.text}</p>
              </div>)}
          </div>

          <div className="mt-10 min-w-0 rounded-2xl bg-white p-5 text-ink sm:p-7" data-id="element-76" data-mp="3r54hvv5qr797">
            <span className="pill bg-terra/15 text-terra" data-id="element-77" data-mp="3w4h4qfwm4ebs">REAL SESSION PREVIEW</span>
            <h3 className="font-display mt-3 text-2xl font-extrabold leading-tight text-sage" data-id="element-78" data-mp="3w2e8he4v9oym">{PREVIEW.title}</h3>
            <p className="mt-3 text-sm" data-id="element-79" data-mp="33ftfyacnn6th"><strong data-id="element-80" data-mp="33glb5k2cb2j8">Goal:</strong> {PREVIEW.goal}</p>
            <p className="mt-1 text-sm" data-id="element-81" data-mp="33dpc1sera7t7"><strong data-id="element-82" data-mp="38emtg2rxyxtn">Best for:</strong> {PREVIEW.bestFor}</p>
            <p className="mt-1 text-sm text-muted" data-id="element-83" data-mp="38d20ghl9zmxw">{PREVIEW.time}</p>
            <p className="mt-4 text-sm font-bold" data-id="element-84" data-mp="2f63lpv55vof0">Core exercise</p>
            <ol className="mt-2 space-y-1.5" data-id="element-85" data-mp="2k5ya9s1gs1ww">
              {PREVIEW.steps.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-86" data-mp="2k8az8p5n3cjg" data-mpi={i}>
                  <span className="shrink-0 font-bold text-terra" data-id="element-87" data-mp="2p6m92zvxpn1o">{i + 1}.</span>
                  <span className="min-w-0 break-words" data-id="element-88" data-mp="2u3cr3srauspq">{s}</span>
                </li>)}
            </ol>
            <p className="mt-4 text-sm font-bold" data-id="element-89" data-mp="1whosr2baa0bk">Powerful questions</p>
            <ul className="mt-2 space-y-1" data-id="element-90" data-mp="21fgvkrdgv6uj">
              {PREVIEW.questions.map(q => <li key={q} className="text-sm italic text-ink/85" data-id="element-91" data-mp="21ddxrl62o9f9">• {q}</li>)}
            </ul>
            <p className="mt-4 rounded-xl bg-creamy p-3 text-xs text-ink/80" data-id="element-92" data-mp="26cql070uviid">…plus the group and online version, the between-session action, the closing question, a coach note and Worksheet 02 for your client.</p>
          </div>
        </div>
      </section>

      {/* 5. BONUSES */}
      <section className="bg-creamy" data-id="element-93" data-mp="1ino10sir1dkp">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-94" data-mp="3vzikq734a9r1">
          <Label data-id="element-95" data-mp="3vyyzohi6758s">Included free · 4 bonuses</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-96" data-mp="3ry6qtpd7n2d1">Plus 4 bonuses for your coaching practice.</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-97" data-mp="3mxj2jbgd8zmi">
            {BONUSES.map((b, i) => <div key={b.title} className="card flex min-w-0 gap-4 p-4" data-id="element-98" data-mp="3hyo79a17pili" data-mpi={i}>
                <div className="w-[38%] max-w-[150px] shrink-0" data-id="element-99" data-mp="3d0edn3v4yq20">
                  <BonusCover index={i} image={BONUS_IMAGES[i] || ''} short={b.short} data-id="element-100" data-mp="3d2r3e374y4pm" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-101" data-mp="388391npgag34">
                  <p className="text-[11px] font-bold text-terra" data-id="element-102" data-mp="349e0c8fcxmpt">BONUS {i + 1}</p>
                  <h3 className="font-display text-lg font-bold leading-tight sm:text-xl" data-id="element-103" data-mp="2za12ytqv0x8z">{b.title}</h3>
                  <p className="mt-1 text-sm text-ink/80" data-id="element-104" data-mp="2uaeropuldr2t">{b.text}</p>
                  <p className="mt-2 text-sm" data-id="element-105" data-mp="2pb21pw8fc3w0">
                    <span className="text-muted line-through" data-id="element-106" data-mp="2pcd5hw1vdbn4">${b.value}</span> <span className="font-bold text-go" data-id="element-107" data-mp="2fnpq8igs53ts">FREE</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="bg-white" data-id="element-108" data-mp="26n1ghzyf5pmp">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-109" data-mp="26o2yyphrleeg">
          <Label data-id="element-110" data-mp="2ao3dea4v5pli">How it works</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-111" data-mp="2ap4q0j1j2k5c">Open it. Pick a session. Coach.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-112" data-mp="2yg0qe4k1uno7">
            {STEPS.map(s => <div key={s.n} className="card min-w-0 p-5" data-id="element-113" data-mp="33cis0vk325wb">
                <span className="font-display text-5xl font-extrabold leading-none text-terra" data-id="element-114" data-mp="2pm5kuvf0mv81">{s.n}</span>
                <h3 className="font-display mt-3 text-xl font-bold" data-id="element-115" data-mp="2tmpjk3f8m6wa">{s.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-116" data-mp="3hd1yhy8wh3wd">{s.text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. OFFER — the ONLY checkout button lives here */}
      <section id="offer" className="bg-sagegrad scroll-mt-4 text-white" data-id="element-117" data-mp="1aojeihg1kkqw">
        <div className="mx-auto max-w-xl px-4 py-14" data-id="element-118" data-mp="1amgfx90tpjak">
          <div className="text-center" data-id="element-119" data-mp="15q85g8xkme9q">
            <Label light data-id="element-120" data-mp="15p6l1duo0ffc">The offer</Label>
            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-121" data-mp="1kevz29nuwdx1">Everything you get</h2>
          </div>
          <div className="mt-6 overflow-hidden rounded-2xl bg-white text-ink shadow-2xl" data-id="element-122" data-mp="1fgkn9te03tc6">
            <div className="p-3" data-id="element-123" data-mp="rqqfhq8jguys">
              <BundleMockup compact data-id="element-124" data-mp="rrrprshqddfg" />
            </div>
            <div className="p-4 pt-0 sm:p-6 sm:pt-0" data-id="element-125" data-mp="mrnj0u6mjx2f">
              <ul className="divide-y divide-line text-sm" data-id="element-126" data-mp="msymesw5r2t0">
                <li className="flex items-center justify-between gap-3 py-3" data-id="element-127" data-mp="11ompgepqpjvb">
                  <span className="min-w-0 font-semibold" data-id="element-128" data-mp="woicgyo1j79u">📘 150+ Done-For-You Coaching Sessions & Worksheets™ · 6 Volumes</span>
                  <span className="shrink-0 text-muted line-through" data-id="element-129" data-mp="2d1grldg2iw26">${ORIGINAL_PRICE}</span>
                </li>
                {BONUSES.map(b => <li key={b.title} className="flex items-center justify-between gap-3 py-3" data-id="element-130" data-mp="hmterdq1h4bn">
                    <span className="flex min-w-0 items-start gap-2" data-id="element-131" data-mp="hp66gillmt1w">
                      <span className="shrink-0 text-base leading-none" data-id="element-132" data-mp="mmg0kbjxnsw2">🎁</span>
                      <span className="min-w-0" data-id="element-133" data-mp="qmq8htractdk">{b.title}</span>
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-right" data-id="element-134" data-mp="vnnoq0sqjdcf">
                      <span className="text-muted line-through" data-id="element-135" data-mp="10m7de8ypr38h">${b.value}</span> <span className="font-bold text-go" data-id="element-136" data-mp="10jl6mbjncrrb">FREE</span>
                    </span>
                  </li>)}
                <li className="flex items-center gap-2 py-3" data-id="element-137" data-mp="15ifq7fpfukch">
                  <span className="shrink-0 font-bold text-go" data-id="element-138" data-mp="1ac0pdb2rpkd9">✔</span>
                  <span className="font-semibold" data-id="element-139" data-mp="1edc6stbhmzad">7-Day Risk-Free Guarantee</span>
                </li>
                <li className="flex items-center gap-2 py-3" data-id="element-140" data-mp="1jcyl7631ylkr">
                  <span className="shrink-0 font-bold text-go" data-id="element-141" data-mp="1ob9spa5vkjzt">✔</span>
                  <span className="font-semibold" data-id="element-142" data-mp="1objhwvj3in4g">Lifetime Access &amp; Free Updates</span>
                </li>
              </ul>
              <div className="mt-4 border-t border-line pt-4 text-center" data-id="element-143" data-mp="1tg3uxszm6iua">
                <p className="text-sm" data-id="element-144" data-mp="1ti6tx2iqvmb5">
                  Total value: <span className="text-muted line-through" data-id="element-145" data-mp="1og94trnnxxaa">${TOTAL_VALUE}</span>
                </p>
                <p className="mt-1 text-sm" data-id="element-146" data-mp="1kg7dabbn6k8q">
                  Regular price <span className="line-through" data-id="element-147" data-mp="1khs35nn0hf09">${ORIGINAL_PRICE}</span> · your price, one-time payment:
                </p>
                <p className="font-display text-7xl font-extrabold leading-none text-sage" data-id="element-148" data-mp="2c63fc2kif9rd">${PRICE}</p>
                <p className="mt-3 text-xs text-muted" data-id="element-149" data-mp="27fd6uggseqsg">· Payment will be processed in your local currency ·</p>
                <button type="button" onClick={goToCheckout} className="mt-3 w-full rounded-full bg-go px-4 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_5px_0_#248a16] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_2px_0_#248a16] sm:text-xl" data-id="element-150" data-mp="23f3194wwq2e4">
                  Get the 78 sessions now
                </button>
                <p className="mt-3 text-[11px] text-muted" data-id="element-151" data-mp="y70ig9wlc9y6">Secure checkout · Instant PDF access</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. GUARANTEE */}
      <section className="bg-creamy" data-id="element-152" data-mp="ogxgvlbelno8">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-153" data-mp="ofw51emkcx5g">
          <div className="card flex flex-col items-center gap-5 p-6 text-center sm:flex-row sm:text-left" data-id="element-154" data-mp="ahfrb3qaa6dt">
            <div className="font-display flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-sage text-3xl font-extrabold text-cream" data-id="element-155" data-mp="fg8zqhqpr774">7</div>
            <div className="min-w-0" data-id="element-156" data-mp="l2uvy3ctefd">
              <h2 className="font-display text-3xl font-extrabold leading-tight" data-id="element-157" data-mp="5i2zs5ogwb4w">7-Day Risk-Free Guarantee</h2>
              <p className="mt-2 text-ink/85" data-id="element-158" data-mp="1uwrb03lbnn1c">
                Open the volumes and run a session with your next client. If the toolkit does not fit your coaching, email us within 7 days of purchase and you get your
                money back.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-white" data-id="element-159" data-mp="3fh4x1gt2j0p6">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-160" data-mp="3u3y2lucscs46">
          <Label data-id="element-161" data-mp="3u5sr1in9n2d0">Questions</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-162" data-mp="3pdhqcdkpbcje">Frequently asked questions</h2>
          <div className="mt-6 border-t border-line" data-id="element-163" data-mp="6yqmv08leflg">
            {FAQ.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-164" data-mp="2xwyy6v5h0sk" />)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA (scrolls to the offer) */}
      <section className="bg-sagegrad text-white" data-id="element-165" data-mp="gwmwfnlvt2hg">
        <div className="mx-auto max-w-2xl px-4 py-14 text-center" data-id="element-166" data-mp="byldr3sdb56i">
          <h2 className="font-display text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-167" data-mp="bz4nvybs0p9z">
            Less time planning.
            <br data-id="element-168" data-mp="2iqvxbvg4zozb" />
            <span className="text-terra" data-id="element-169" data-mp="2dq7veeqddnqb">More time coaching.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-white/85" data-id="element-170" data-mp="2dsaxvy8pl9bx">78 sessions, 78 worksheets and 4 bonuses, ready for your next client, group or workshop.</p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-171" data-mp="1wuu2ix4jzfuj">
            <SeeButton text="See the complete system now" data-id="element-172" data-mp="21mlsb5fvxhjm" />
          </div>
        </div>
      </section>

      {/* 11. FOOTER */}
      <footer className="border-t border-line bg-white pb-24 md:pb-10" data-id="element-173" data-mp="26lydzmv0sikm">
        <div className="mx-auto max-w-3xl px-4 pt-10 text-center" data-id="element-174" data-mp="18xy34ys0m80s">
          <p className="font-display text-2xl font-extrabold tracking-wide text-sage" data-id="element-175" data-mp="18zrakgagcw9u">
            SIGMA SMART<span className="text-terra" data-id="element-176" data-mp="1dx9n0gnohtml">™</span>
          </p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-177" data-mp="1ivukw9fgk17v">
            <BundleMockup compact data-id="element-178" data-mp="1iy6zccm0btg6" />
          </div>
          <p className="mt-6 text-sm" data-id="element-179" data-mp="1nw8kr46wo4ut">
            📧{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="break-all font-semibold underline" data-id="element-180" data-mp="2ub9jfhnbf153">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p className="mt-2 text-sm" data-id="element-181" data-mp="2z917g0kua340">
            📲{' '}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-id="element-182" data-mp="nmr1e6e0os29">
              {INSTAGRAM_HANDLE}
            </a>
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-xs leading-relaxed text-muted" data-id="element-183" data-mp="iqa34jd3sxbg">
            Disclaimer: this is a digital toolkit of coaching session guides and worksheets for professional coaches, for educational purposes. It is non-clinical and is
            not therapy, counseling, medical or psychological advice. It does not guarantee any result for you or your clients. Refer clients who need mental health
            support to a licensed professional.
          </p>
          <p className="mt-4 text-[11px] text-muted" data-id="element-184" data-mp="3r3zgwjl9vftt">© {new Date().getFullYear()} SIGMA SMART™ · All rights reserved.</p>
        </div>
      </footer>

      {/* 12. MOBILE STICKY BAR (scrolls to the offer only) */}
      <StickyBar data-id="element-185" data-mp="1pa07k3rpn8jr" />
    </div>;
}